// Pages/Profile/Profile.jsx
import React, { useState, useEffect, useRef } from 'react';
import { User, Mail, Phone, Home, Calendar, Save, X, Edit3, Camera, Upload, Trash } from 'lucide-react';
import { getCurrentProfile, updateUserProfile, getUserBuildings, updateUserBuilding } from '../../Fetch/fetchUser';
import { uploadProfilePicture, deleteProfilePicture, getProfilePictureAsBlob } from '../../Fetch/fetchUploadImage';
import { enhancedTokenManager } from '../../utils/enhancedTokenManager';
import styles from './Profile.module.css';

const Profile = ({ userData }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);
    const [userBuildingId, setUserBuildingId] = useState(null);

    // Profile picture upload states
    const [isUploadingProfilePic, setIsUploadingProfilePic] = useState(false);
    const [profilePicturePreview, setProfilePicturePreview] = useState(null);

    const [profileData, setProfileData] = useState({
        fullName: '',
        email: '',
        phone: '',
        buildingAddress: '',
        buildingCity: '',
        joinDate: '',
        role: [],
        userID: '',
        profilePicture: '',
        profileImagePath: '' // Updated field name to match database
    });
    const [formData, setFormData] = useState({ ...profileData });

    // File upload refs
    const profilePicFileRef = useRef(null);
    const profilePicCameraRef = useRef(null);

    // Role options for multiselect
    const roleOptions = [
        { value: 'tenant', label: 'דייר', dbField: 'IsTenant' },
        { value: 'vaad', label: 'חבר ועד', dbField: 'IsVaad' },
        { value: 'manager', label: 'מנהל', dbField: 'IsManager' }
    ];

    useEffect(() => {
        loadUserProfile();
    }, [userData]);

    // Add event listener for user data changes - FIXED to prevent infinite loop
    useEffect(() => {
        const handleUserDataChange = (event) => {
            // Only reload if we're not currently saving AND the event didn't come from this component
            if (!isSaving && !event.detail?.fromProfile) {
                loadUserProfile();
            }
        };

        window.addEventListener('userDataChanged', handleUserDataChange);

        return () => {
            window.removeEventListener('userDataChanged', handleUserDataChange);
        };
    }, [isSaving]);

    // Helper function to parse combined address
    const parseAddress = (fullAddress) => {
        if (!fullAddress) return { street: '', number: '', city: '' };

        const parts = fullAddress.split(',');
        if (parts.length === 2) {
            const streetPart = parts[0].trim();
            const city = parts[1].trim();

            const streetWords = streetPart.split(' ');
            const lastWord = streetWords[streetWords.length - 1];

            if (/^\d+$/.test(lastWord)) {
                const street = streetWords.slice(0, -1).join(' ');
                return { street, number: lastWord, city };
            } else {
                return { street: streetPart, number: '', city };
            }
        }

        return { street: fullAddress, number: '', city: '' };
    };

    // Helper function to determine roles from database fields OR localStorage
    const determineRolesFromDbFields = (userBuilding, localUserData = {}) => {
        const roles = [];

        // Check building data first
        if (userBuilding) {
            // Check for Manager role - handle different formats
            if (userBuilding.IsManager === 'כן' || userBuilding.isManager === 'כן' || 
                userBuilding.IsManager === true || userBuilding.isManager === true ||
                userBuilding.IsManager === 'true' || userBuilding.isManager === 'true') {
                roles.push('manager');
            }
            // Check for Vaad role - handle different formats  
            if (userBuilding.IsVaad === 'כן' || userBuilding.isVaad === 'כן' ||
                userBuilding.IsVaad === true || userBuilding.isVaad === true ||
                userBuilding.IsVaad === 'true' || userBuilding.isVaad === 'true') {
                roles.push('vaad');
            }
            // Check for Tenant role - handle different formats
            if (userBuilding.IsTenant === 'כן' || userBuilding.isTenant === 'כן' ||
                userBuilding.IsTenant === true || userBuilding.isTenant === true ||
                userBuilding.IsTenant === 'true' || userBuilding.isTenant === 'true') {
                roles.push('tenant');
            }
        }

        // If no roles found in building data, check localStorage
        if (roles.length === 0 && localUserData) {
            if (localUserData.ismanager === 'כן' || localUserData.isManager === 'כן' ||
                localUserData.userRoles?.isManager === 'כן') {
                roles.push('manager');
            }
            if (localUserData.isvaad === 'כן' || localUserData.isVaad === 'כן' ||
                localUserData.userRoles?.isVaad === 'כן') {
                roles.push('vaad');
            }
            if (localUserData.istenant === 'כן' || localUserData.isTenant === 'כן' ||
                localUserData.userRoles?.isTenant === 'כן') {
                roles.push('tenant');
            }
        }

        return roles.length > 0 ? roles : ['tenant']; // Default to tenant if no roles
    };

    // Helper function to get role labels (for display)
    const getRoleLabels = (roleValues) => {
        if (!Array.isArray(roleValues)) return 'דייר';
        const labels = roleValues.map(roleValue => {
            const role = roleOptions.find(option => option.value === roleValue);
            return role ? role.label : '';
        }).filter(label => label);
        return labels.length > 0 ? labels.join(', ') : 'דייר';
    };

    // Profile picture upload handlers - UPDATED TO USE UTILITY FUNCTIONS
    const handleProfilePictureUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const userData = enhancedTokenManager.getUserData();
        const currentBuildingId = userData?.buildingId;
        const userId = userData?.userId || profileData.userID;

        if (currentBuildingId === null || currentBuildingId === undefined) {
            alert('שגיאה: לא נמצא מזהה בניין. אנא התחבר מחדש.');
            return;
        }

        if (!userId) {
            alert('שגיאה: לא נמצא מזהה משתמש. אנא התחבר מחדש.');
            return;
        }

        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
        if (!allowedTypes.includes(file.type)) {
            alert('סוג קובץ לא נתמך. אנא בחר תמונה (JPG, PNG, GIF).');
            return;
        }

        const maxSize = 5 * 1024 * 1024; // 5MB for profile pictures
        if (file.size > maxSize) {
            alert('הקובץ גדול מדי. גודל מקסימלי: 5MB');
            return;
        }

        setIsUploadingProfilePic(true);

        try {
            const uploadResult = await uploadProfilePicture(file, currentBuildingId, userId);

            if (uploadResult.success) {
                const base64 = await convertFileToBase64(file);

                setFormData(prev => ({
                    ...prev,
                    profilePicture: base64,
                    profileImagePath: uploadResult.data.filePath
                }));

                setProfilePicturePreview(base64);

                // UPDATED: Dispatch specific profile picture update event
                window.dispatchEvent(new CustomEvent('profilePictureUpdated', {
                    detail: {
                        profilePictureUpdated: true,
                        newImagePath: uploadResult.data.filePath,
                        newBase64: base64
                    }
                }));

                alert('תמונת פרופיל הועלתה בהצלחה!');
            } else {
                throw new Error(uploadResult.message || 'Upload failed');
            }

        } catch (error) {
            if (error.message.includes('401')) {
                alert('שגיאה באימות. אנא התחבר מחדש.');
            } else {
                alert('שגיאה בהעלאת תמונת הפרופיל: ' + error.message);
            }
        } finally {
            setIsUploadingProfilePic(false);
        }
    };



    const handleProfilePictureCamera = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const userData = enhancedTokenManager.getUserData();
        const currentBuildingId = userData?.buildingId;
        const userId = userData?.userId || profileData.userID;

        if (currentBuildingId === null || currentBuildingId === undefined) {
            alert('שגיאה: לא נמצא מזהה בניין. אנא התחבר מחדש.');
            return;
        }

        if (!userId) {
            alert('שגיאה: לא נמצא מזהה משתמש. אנא התחבר מחדש.');
            return;
        }

        setIsUploadingProfilePic(true);

        try {
            const uploadResult = await uploadProfilePicture(file, currentBuildingId, userId);

            if (uploadResult.success) {
                const base64 = await convertFileToBase64(file);

                setFormData(prev => ({
                    ...prev,
                    profilePicture: base64,
                    profileImagePath: uploadResult.data.filePath
                }));

                setProfilePicturePreview(base64);

                // UPDATED: Dispatch specific profile picture update event
                window.dispatchEvent(new CustomEvent('profilePictureUpdated', {
                    detail: {
                        profilePictureUpdated: true,
                        newImagePath: uploadResult.data.filePath,
                        newBase64: base64
                    }
                }));

                alert('תמונת פרופיל נצולמה והועלתה בהצלחה!');
            } else {
                throw new Error(uploadResult.message || 'Upload failed');
            }

        } catch (error) {
            if (error.message.includes('401')) {
                alert('שגיאה באימות. אנא התחבר מחדש.');
            } else {
                alert('שגיאה בצילום תמונת הפרופיל: ' + error.message);
            }
        } finally {
            setIsUploadingProfilePic(false);
        }
    };

    const convertFileToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = (error) => reject(error);
            reader.readAsDataURL(file);
        });
    };


    const removeProfilePicture = async () => {
        if (formData.profileImagePath) {
            try {
                const userData = enhancedTokenManager.getUserData();
                const userId = userData?.userId || profileData.userID;

                await deleteProfilePicture(formData.profileImagePath, userId);
            } catch (error) {
            }
        }

        setFormData(prev => ({
            ...prev,
            profilePicture: '',
            profileImagePath: ''
        }));

        setProfilePicturePreview(null);

        // UPDATED: Dispatch specific profile picture update event for removal
        window.dispatchEvent(new CustomEvent('profilePictureUpdated', {
            detail: {
                profilePictureUpdated: true,
                removed: true
            }
        }));

        if (profilePicFileRef.current) profilePicFileRef.current.value = '';
        if (profilePicCameraRef.current) profilePicCameraRef.current.value = '';
    };


    const loadUserProfile = async () => {
        setIsLoading(true);
        setError(null);
        try {
            // Get user data from enhancedTokenManager first
            let localUserData = enhancedTokenManager.getUserData() || {};

            // Fetch additional profile data from API if needed
            let apiProfileData = {};
            if (userData?.email || localUserData.email) {
                try {
                    const email = userData?.email || localUserData.email;
                    apiProfileData = await getCurrentProfile({ email });
                } catch (apiError) {
                }
            }

            // Get building information for address display only
            let buildingAddress = '';
            let buildingCity = '';
            let currentUserBuildingId = null;
            let userRoles = ['tenant'];

            try {
                if (localUserData.userId) {
                    const buildings = await getUserBuildings(localUserData.userId);
                if (buildings && buildings.length > 0) {
                    const currentBuilding = buildings.find(b => 
                        b.buildingId === localUserData.buildingId || 
                        b.buildingID === localUserData.buildingId
                    ) || buildings[0];

                    // Parse address for display
                    const rawAddress = currentBuilding.address || currentBuilding.Address || '';
                    const parsedAddress = parseAddress(rawAddress);

                    buildingAddress = parsedAddress.street && parsedAddress.number
                        ? `${parsedAddress.street} ${parsedAddress.number}`
                        : (currentBuilding.street || currentBuilding.Street || 'לא הוגדר');

                    buildingCity = parsedAddress.city || currentBuilding.city || currentBuilding.City || 'לא הוגדר';

                    currentUserBuildingId = currentBuilding.userBuildingId ||
                        currentBuilding.userBuildingID ||
                        currentBuilding.UserBuildingID ||
                        currentBuilding.id;

                    // Debug: Log the current building data to understand the structure
                    console.log('Profile.jsx - Current building data:', currentBuilding);
                    
                    // Determine roles from database fields + localStorage fallback
                    userRoles = determineRolesFromDbFields(currentBuilding, localUserData);
                    console.log('Profile.jsx - Determined roles:', userRoles);
                }
                } else {
                }
            } catch (buildingError) {

                // Fallback to localStorage building data
                if (localUserData.selectedBuildingInfo || localUserData.currentBuilding) {
                    const fallbackBuilding = localUserData.selectedBuildingInfo || {};
                    const currentBuilding = localUserData.currentBuilding || {};

                    const addressToParse = currentBuilding.address || '';
                    const parsedAddress = parseAddress(addressToParse);

                    buildingAddress = parsedAddress.street && parsedAddress.number
                        ? `${parsedAddress.street} ${parsedAddress.number}`
                        : (fallbackBuilding.address || 'לא הוגדר');

                    buildingCity = parsedAddress.city || fallbackBuilding.city || 'לא הוגדר';

                    userRoles = determineRolesFromDbFields(null, localUserData);
                }
            }

            setUserBuildingId(currentUserBuildingId);

            // Enhanced profile data mapping - UPDATED FIELD MAPPING
            const profileInfo = {
                fullName: apiProfileData?.FullName ||
                    apiProfileData?.fullName ||
                    apiProfileData?.name ||
                    localUserData.name ||
                    localUserData.fullName ||
                    userData?.name || '',

                email: localUserData.email ||
                    apiProfileData?.Email ||
                    apiProfileData?.email ||
                    userData?.email || '',

                phone: apiProfileData?.PhoneNumber ||
                    apiProfileData?.Phone ||
                    apiProfileData?.phone ||
                    localUserData.phone ||
                    localUserData.phoneNumber || '',


                buildingAddress: buildingAddress,
                buildingCity: buildingCity,

                joinDate: apiProfileData?.CreatedDate ||
                    apiProfileData?.createDate ||
                    apiProfileData?.joinDate ||
                    localUserData.joinDate ||
                    localUserData.createdDate ||
                    new Date().toISOString().split('T')[0],

                role: userRoles,

                userID: apiProfileData?.UserID ||
                    apiProfileData?.userId ||
                    apiProfileData?.id ||
                    apiProfileData?.Id ||
                    localUserData.userId ||
                    localUserData.id || '',

                profilePicture: apiProfileData?.ProfilePicture ||
                    apiProfileData?.profilePicture ||
                    localUserData.profilePicture || '',

                profileImagePath: apiProfileData?.ProfileImagePath ||
                    apiProfileData?.profileImagePath ||
                    localUserData.profileImagePath || ''
            };

            setProfileData(profileInfo);

            if (!isEditing) {
                setFormData(profileInfo);
            }

            // IMPORTANT: Update user data immediately with API data (especially profileImagePath)
            // This ensures Layout component can access the latest profile picture data
            if (localUserData && (profileInfo.profileImagePath || profileInfo.profilePicture)) {
                const user = localUserData;
                const updatedUser = {
                    ...user,
                    name: profileInfo.fullName || user.name,
                    email: profileInfo.email || user.email,
                    phone: profileInfo.phone || user.phone,
                    profilePicture: profileInfo.profilePicture || user.profilePicture,
                    profileImagePath: profileInfo.profileImagePath || user.profileImagePath
                };

                enhancedTokenManager.setUserData(updatedUser);

                // Trigger event to notify Layout component - MARKED as from Profile to prevent loop
                setTimeout(() => {
                    window.dispatchEvent(new CustomEvent('userDataChanged', {
                        detail: { fromProfile: true }
                    }));
                }, 100);
            }

            // Set profile picture preview - UPDATED TO USE UTILITY FUNCTIONS
            if (profileInfo.profileImagePath) {
                // Try to load from server path first using utility function
                try {
                    const blobUrl = await getProfilePictureAsBlob(profileInfo.profileImagePath);
                    setProfilePicturePreview(blobUrl);
                } catch (imageError) {
                    // Fallback to base64 if available
                    if (profileInfo.profilePicture) {
                        setProfilePicturePreview(profileInfo.profilePicture);
                    } else {
                        setProfilePicturePreview(null);
                    }
                }
            } else if (profileInfo.profilePicture) {
                // Use base64 data if no server path
                setProfilePicturePreview(profileInfo.profilePicture);
            } else {
                setProfilePicturePreview(null);
            }

        } catch (error) {
            setError('שגיאה בטעינת פרטי הפרופיל');
        } finally {
            setIsLoading(false);
        }
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Special handler for role multiselect
    const handleRoleChange = (roleValue) => {
        setFormData(prev => {
            const currentRoles = prev.role || [];
            const isSelected = currentRoles.includes(roleValue);

            let newRoles;
            if (isSelected) {
                newRoles = currentRoles.filter(role => role !== roleValue);
            } else {
                newRoles = [...currentRoles, roleValue];
            }

            // Ensure at least one role is selected (default to tenant)
            if (newRoles.length === 0) {
                newRoles = ['tenant'];
            }

            return {
                ...prev,
                role: newRoles
            };
        });
    };


// Updated handleSave function for Profile.jsx
const handleSave = async () => {
    setIsSaving(true);

    const currentRoles = Array.isArray(formData.role) ? formData.role : ['tenant'];
    setError(null);

    try {
        let userUpdateSuccess = false;
        let userBuildingUpdateSuccess = false;

        // Get the buildingId from enhancedTokenManager
        const localUserData = enhancedTokenManager.getUserData();
        let buildingId = localUserData?.buildingId || null;

        // Update user profile data using the new profile-specific function
        try {
            const updateData = {
                UserID: profileData.userID,
                FullName: formData.fullName,
                Email: formData.email,
                Phone: formData.phone,
                BuildingID: buildingId,
                
                // Pass roles array - will be converted to Hebrew strings in fetchUser
                role: currentRoles,
                
                // Profile picture fields
                ProfilePicture: formData.profilePicture,
                ProfileImagePath: formData.profileImagePath,
                
                // Add _users field for backend compatibility
                _users: []
            };

            
            // Use the profile-specific update function
            await updateUserProfile(updateData);
            userUpdateSuccess = true;
        } catch (userError) {
            throw new Error('שגיאה בעדכון פרטי המשתמש: ' + userError.message);
        }

        // Update UserBuilding role information if needed and userBuildingId exists
        if (userBuildingId && JSON.stringify(formData.role) !== JSON.stringify(profileData.role)) {
            try {
                const roleMapping = {
                    IsManager: formData.role.includes('manager') ? 'true' : 'false',
                    IsVaad: formData.role.includes('vaad') ? 'true' : 'false',
                    IsTenant: formData.role.includes('tenant') ? 'true' : 'false'
                };

                const userBuildingUpdateData = {
                    UserBuildingID: userBuildingId,
                    BuildingID: buildingId,
                    ...roleMapping
                };

                await updateUserBuilding(userBuildingUpdateData);
                userBuildingUpdateSuccess = true;
            } catch (userBuildingError) {
                // Don't throw error here - profile update was successful
            }
        }

        // Update local state
        setProfileData(formData);
        setIsEditing(false);

        // Update user data
        if (localUserData) {
            const user = localUserData;
            const updatedUser = {
                ...user,
                name: formData.fullName,
                email: formData.email,
                phone: formData.phone,
                role: formData.role,
                buildingId: buildingId,
                profilePicture: formData.profilePicture,
                profileImagePath: formData.profileImagePath
            };
            enhancedTokenManager.setUserData(updatedUser);

            // Only dispatch profile picture event if profile picture was changed
            const profilePictureChanged = 
                formData.profilePicture !== profileData.profilePicture ||
                formData.profileImagePath !== profileData.profileImagePath;
                
            if (profilePictureChanged) {
                setTimeout(() => {
                    window.dispatchEvent(new CustomEvent('profilePictureUpdated', { 
                        detail: { 
                            profilePictureUpdated: true,
                            fromSave: true
                        } 
                    }));
                }, 100);
            }
        }

        alert('פרטי המשתמש עודכנו בהצלחה!');
    } catch (error) {
        
        // Handle specific error messages
        let errorMessage = 'שגיאה בעדכון הפרטים';
        
        if (error.message) {
            if (error.message.includes('_users field is required')) {
                errorMessage = 'שגיאה בפורמט הנתונים. אנא נסה שוב.';
            } else if (error.message.includes('could not be converted to System.String')) {
                errorMessage = 'שגיאה בפורמט נתוני התפקידים. אנא רענן את הדף ונסה שוב.';
            } else if (error.message.includes('Network error')) {
                errorMessage = 'שגיאת רשת. אנא בדוק את החיבור לאינטרנט ונסה שוב.';
            } else {
                errorMessage = error.message;
            }
        }
        
        setError(errorMessage);
    } finally {
        setIsSaving(false);
    }
};


    const handleCancel = () => {
        setFormData(profileData);
        setIsEditing(false);
        setError(null);

        // Reset profile picture preview to original
        if (profileData.profileImagePath) {
            // Reload from server path
            loadUserProfile();
        } else if (profileData.profilePicture) {
            setProfilePicturePreview(profileData.profilePicture);
        } else {
            setProfilePicturePreview(null);
        }

        // Clear file inputs
        if (profilePicFileRef.current) profilePicFileRef.current.value = '';
        if (profilePicCameraRef.current) profilePicCameraRef.current.value = '';
    };

    if (isLoading) {
        return (
            <div className={styles.container}>
                <div className={styles.loading}>
                    <p>טוען פרטי פרופיל...</p>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h1 className={styles.title}>
                    <User size={24} className={styles.titleIcon} />
                    פרופיל אישי
                </h1>
                <button
                    className={styles.editButton}
                    onClick={() => setIsEditing(!isEditing)}
                    disabled={isSaving}
                >
                    {isEditing ? (
                        <>
                            <X size={16} />
                            ביטול
                        </>
                    ) : (
                        <>
                            <Edit3 size={16} />
                            עריכה
                        </>
                    )}
                </button>
            </div>

            {error && (
                <div className={styles.errorMessage}>
                    {error.split('\n').map((line, index) => (
                        <div key={index}>{line}</div>
                    ))}
                </div>
            )}

            <div className={styles.content}>
                <div className={styles.profileCard}>
                    {/* Profile Picture Section */}
                    <div className={styles.profilePictureSection}>
                        <div className={styles.profilePicture}>
                            {profilePicturePreview || formData.profilePicture ? (
                                <img
                                    src={profilePicturePreview || formData.profilePicture}
                                    alt="תמונת פרופיל"
                                    className={styles.profileImage}
                                    onError={() => {
                                        setProfilePicturePreview(null);
                                    }}
                                />
                            ) : (
                                <User size={40} />
                            )}
                        </div>

                        {isEditing && (
                            <div className={styles.profilePictureActions}>
                                {/* Hidden file inputs */}
                                <input
                                    type="file"
                                    ref={profilePicFileRef}
                                    onChange={handleProfilePictureUpload}
                                    accept="image/*"
                                    style={{ display: 'none' }}
                                />

                                <input
                                    type="file"
                                    ref={profilePicCameraRef}
                                    onChange={handleProfilePictureCamera}
                                    accept="image/*"
                                    capture="environment"
                                    style={{ display: 'none' }}
                                />

                                <div className={styles.uploadButtons}>
                                    <button
                                        type="button"
                                        onClick={() => profilePicFileRef.current?.click()}
                                        className={styles.uploadButton}
                                        disabled={isUploadingProfilePic}
                                    >
                                        <Upload size={16} />
                                        בחר תמונה
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => profilePicCameraRef.current?.click()}
                                        className={styles.cameraButton}
                                        disabled={isUploadingProfilePic}
                                    >
                                        <Camera size={16} />
                                        צלם תמונה
                                    </button>

                                    {(profilePicturePreview || formData.profilePicture) && (
                                        <button
                                            type="button"
                                            onClick={removeProfilePicture}
                                            className={styles.removeButton}
                                            disabled={isUploadingProfilePic}
                                        >
                                            <Trash size={16} />
                                            הסר תמונה
                                        </button>
                                    )}
                                </div>

                                {isUploadingProfilePic && (
                                    <div className={styles.uploadStatus}>
                                        <span className={styles.spinner}>⏳</span>
                                        מעלה תמונת פרופיל...
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Profile Information */}
                    <div className={styles.profileInfo}>
                        <div className={styles.infoGrid}>
                            {/* Full Name */}
                            <div className={styles.infoItem}>
                                <label className={styles.infoLabel}>
                                    <User size={16} />
                                    שם מלא
                                </label>
                                {isEditing ? (
                                    <input
                                        type="text"
                                        value={formData.fullName}
                                        onChange={(e) => handleInputChange('fullName', e.target.value)}
                                        className={styles.infoInput}
                                        placeholder="הכנס שם מלא"
                                    />
                                ) : (
                                    <span className={styles.infoValue}>
                                        {profileData.fullName || 'לא הוגדר'}
                                    </span>
                                )}
                            </div>

                            {/* Email */}
                            <div className={styles.infoItem}>
                                <label className={styles.infoLabel}>
                                    <Mail size={16} />
                                    אימייל
                                </label>
                                {isEditing ? (
                                    <input
                                        type="email"
                                        value={formData.email}
                                        onChange={(e) => handleInputChange('email', e.target.value)}
                                        className={styles.infoInput}
                                        placeholder="הכנס כתובת אימייל"
                                    />
                                ) : (
                                    <span className={styles.infoValue}>
                                        {profileData.email || 'לא הוגדר'}
                                    </span>
                                )}
                            </div>

                            {/* Phone */}
                            <div className={styles.infoItem}>
                                <label className={styles.infoLabel}>
                                    <Phone size={16} />
                                    טלפון
                                </label>
                                {isEditing ? (
                                    <input
                                        type="tel"
                                        value={formData.phone}
                                        onChange={(e) => handleInputChange('phone', e.target.value)}
                                        className={styles.infoInput}
                                        placeholder="הכנס מספר טלפון"
                                    />
                                ) : (
                                    <span className={styles.infoValue}>
                                        {profileData.phone || 'לא הוגדר'}
                                    </span>
                                )}
                            </div>


                            {/* Role - Multiselect */}
                            <div className={styles.infoItem}>
                                <label className={styles.infoLabel}>
                                    <User size={16} />
                                    תפקיד
                                </label>
                                {isEditing ? (
                                    <div className={styles.roleCheckboxContainer}>
                                        {roleOptions.map((option) => (
                                            <label key={option.value} className={styles.roleCheckboxLabel}>
                                                <input
                                                    type="checkbox"
                                                    checked={formData.role.includes(option.value)}
                                                    onChange={() => handleRoleChange(option.value)}
                                                    className={styles.roleCheckbox}
                                                />
                                                <span className={styles.roleCheckboxText}>{option.label}</span>
                                            </label>
                                        ))}
                                    </div>
                                ) : (
                                    <span className={styles.infoValue}>
                                        {getRoleLabels(profileData.role)}
                                    </span>
                                )}
                            </div>

                            {/* Building Address - Read Only */}
                            <div className={styles.infoItem}>
                                <label className={styles.infoLabel}>
                                    <Home size={16} />
                                    כתובת הבניין
                                </label>
                                <span className={styles.infoValue}>
                                    {profileData.buildingAddress || 'לא הוגדר'}
                                </span>
                            </div>

                            {/* City - Read Only */}
                            <div className={styles.infoItem}>
                                <label className={styles.infoLabel}>
                                    <Calendar size={16} />
                                    עיר
                                </label>
                                <span className={styles.infoValue}>
                                    {profileData.buildingCity || 'לא הוגדר'}
                                </span>
                            </div>

                            {/* Join Date - Read Only */}
                            <div className={styles.infoItem}>
                                <label className={styles.infoLabel}>
                                    <Calendar size={16} />
                                    תאריך הצטרפות
                                </label>
                                <span className={styles.infoValue}>
                                    {profileData.joinDate ?
                                        new Date(profileData.joinDate).toLocaleDateString('he-IL') :
                                        'לא הוגדר'
                                    }
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Save/Cancel Actions - Bottom of Page */}
                {isEditing && (
                    <div className={styles.bottomActions}>
                        <button
                            className={styles.saveButton}
                            onClick={handleSave}
                            disabled={isSaving}
                        >
                            <Save size={16} />
                            {isSaving ? 'שומר...' : 'שמור שינויים'}
                        </button>
                        <button
                            className={styles.cancelButton}
                            onClick={handleCancel}
                            disabled={isSaving}
                        >
                            <X size={16} />
                            ביטול
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Profile;